import React, { useMemo, useState } from "react";
import { Button, Checkbox, Form, Modal, Radio, Tooltip } from "antd";
import { EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  getRegionCardSummary,
  getRegionDisplayName,
  isRegionFullySelected,
  toggleRegionCountries,
} from "../../../../utils/intlRegion";
import styles from "../index.modules.less";

const getSelectedCountryLabels = (region, selectedCodes = []) => {
  const nameMap = Object.fromEntries(
    (region.country_code || []).map((item) => [
      item.country_code,
      item.country_name || item.country_code,
    ])
  );
  return (region.country_code || [])
    .map((item) => item.country_code)
    .filter((code) => selectedCodes.includes(code))
    .map((code) => nameMap[code] || code);
};

const CountrySelectModal = ({
  open,
  region,
  selectedCountryCodes,
  onCancel,
  onConfirm,
}) => {
  const [draftCodes, setDraftCodes] = useState([]);

  React.useEffect(() => {
    if (!open || !region) {
      return;
    }
    const regionCodes = (region.country_code || []).map((item) => item.country_code);
    setDraftCodes(selectedCountryCodes.filter((code) => regionCodes.includes(code)));
  }, [open, region, selectedCountryCodes]);

  if (!region) {
    return null;
  }

  const options = region.country_code || [];

  return (
    <Modal
      open={open}
      centered
      width={480}
      destroyOnClose
      className={styles.regionSelectModal}
      title={`选择精确地区 · ${getRegionDisplayName(region)}`}
      onCancel={onCancel}
      footer={
        <div className={styles.regionModalFooter}>
          <Button type="primary" onClick={() => onConfirm(draftCodes)}>
            确定
          </Button>
        </div>
      }
    >
      <Checkbox.Group
        value={draftCodes}
        onChange={setDraftCodes}
        className={styles.regionModalCountryGrid}
      >
        {options.map((item) => (
          <div key={item.country_code} className={styles.regionModalCountryItem}>
            <Checkbox value={item.country_code}>
              {item.country_name || item.country_code}
            </Checkbox>
          </div>
        ))}
      </Checkbox.Group>
    </Modal>
  );
};

const RegionSection = ({
  regionOptions,
  portal11Status,
  regionMode,
  setRegionMode,
  selectedCountryCodes,
  setSelectedCountryCodes,
  onRetryLoadOptions,
}) => {
  const [editingRegion, setEditingRegion] = useState(null);

  const regionCards = useMemo(
    () =>
      regionOptions.map((region) => {
        const codes = (region.country_code || []).map((item) => item.country_code);
        const selectedInRegion = selectedCountryCodes.filter((code) => codes.includes(code));
        const selectedLabels = getSelectedCountryLabels(region, selectedInRegion);
        const isAllSelected = isRegionFullySelected(codes, selectedCountryCodes);
        const hasSelection = selectedInRegion.length > 0;
        const summary = getRegionCardSummary(region, selectedLabels);
        const joinedSelectedLabels = selectedLabels.join(", ");
        const showSelectionTip =
          hasSelection && !isAllSelected && joinedSelectedLabels.length > 20;

        return {
          ...region,
          codes,
          selectedInRegion,
          selectedLabels,
          isAllSelected,
          hasSelection,
          summary,
          joinedSelectedLabels,
          tooltipLabels: selectedLabels.join("、"),
          showSelectionTip,
        };
      }),
    [regionOptions, selectedCountryCodes]
  );

  if (portal11Status === "failed") {
    return (
      <div className={styles.placeholder}>
        <div>地区枚举加载失败，无法配置生效地区</div>
        <Button type="link" onClick={onRetryLoadOptions}>
          重试
        </Button>
      </div>
    );
  }

  if (portal11Status === "loading") {
    return <div className={styles.placeholder}>地区枚举加载中...</div>;
  }

  const handleToggleRegion = (region) => {
    setSelectedCountryCodes((prev) => toggleRegionCountries(region.codes, prev));
  };

  const handleModalConfirm = (draftCodes) => {
    if (!editingRegion) {
      return;
    }
    const regionCodes = editingRegion.codes || [];
    setSelectedCountryCodes((prev) => {
      const withoutRegion = prev.filter((code) => !regionCodes.includes(code));
      return [...withoutRegion, ...draftCodes];
    });
    setEditingRegion(null);
  };

  return (
    <>
      <Form.Item
        label="生效地区"
        htmlFor={undefined}
        required
        className={styles.regionModeItem}
      >
        <Radio.Group
          value={regionMode}
          onChange={(event) => setRegionMode(event.target.value)}
        >
          <Radio value="global">全球分发</Radio>
          <Radio value="custom">指定区域</Radio>
        </Radio.Group>
      </Form.Item>

      {regionMode === "custom" ? (
        <Form.Item colon={false} label=" " className={styles.regionPanelItem}>
          <div className={styles.regionPanel}>
            <div className={styles.regionHint}>
              <InfoCircleOutlined className={styles.regionHintIcon} />
              为方便操作，目前提供按大区快捷选定。如需精确剔除个别国家，可点击编辑按钮。
            </div>

            <div className={styles.regionGrid}>
              {regionCards.map((region) => (
                <div
                  key={region.region_code}
                  className={
                    region.hasSelection
                      ? `${styles.regionCard} ${styles.regionCardActive}`
                      : styles.regionCard
                  }
                  onClick={() => handleToggleRegion(region)}
                >
                  <div className={styles.regionCardHead}>
                    <div className={styles.regionCardTitle}>{getRegionDisplayName(region)}</div>
                    <button
                      type="button"
                      className={
                        region.hasSelection
                          ? `${styles.regionEditBtn} ${styles.regionEditBtnActive}`
                          : styles.regionEditBtn
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditingRegion(region);
                      }}
                    >
                      <EditOutlined />
                      精确选择
                    </button>
                  </div>

                  <div className={styles.regionCardDescRow}>
                    <div
                      className={
                        region.hasSelection
                          ? `${styles.regionCardDesc} ${styles.regionCardDescActive}`
                          : styles.regionCardDesc
                      }
                    >
                      {region.summary}
                    </div>
                    {region.showSelectionTip ? (
                      <Tooltip title={region.tooltipLabels}>
                        <InfoCircleOutlined className={styles.regionCardInfoIcon} />
                      </Tooltip>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {!selectedCountryCodes.length ? (
              <div className={styles.regionError}>指定区域模式下请至少选择一个国家/地区</div>
            ) : null}
          </div>
        </Form.Item>
      ) : null}

      <CountrySelectModal
        open={Boolean(editingRegion)}
        region={editingRegion}
        selectedCountryCodes={selectedCountryCodes}
        onCancel={() => setEditingRegion(null)}
        onConfirm={handleModalConfirm}
      />
    </>
  );
};

export default RegionSection;
